import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { FeedReactor } from '../../types';
import './ReactionBar.css';

interface ReactionBarProps {
  targetType: 'activity' | 'feed_post';
  targetId: number;
  clubupCount: number;
  assCount: number;
  hasClubup: boolean;
  hasAss: boolean;
  commentCount: number;
  recentReactors: FeedReactor[];
  aiComment: string | null;
  onCommentClick: () => void;
}

const ReactionBar: React.FC<ReactionBarProps> = ({
  targetType, targetId,
  clubupCount: initialClubup, assCount: initialAss,
  hasClubup: initialHasClubup, hasAss: initialHasAss,
  commentCount, recentReactors, aiComment, onCommentClick,
}) => {
  const { t } = useLanguage();
  const [clubupCount, setClubupCount] = useState(initialClubup);
  const [assCount, setAssCount] = useState(initialAss);
  const [hasClubup, setHasClubup] = useState(initialHasClubup);
  const [hasAss, setHasAss] = useState(initialHasAss);

  const toggleReaction = async (reactionType: 'clubup' | 'ass') => {
    const isClubup = reactionType === 'clubup';
    const wasActive = isClubup ? hasClubup : hasAss;

    // Optimistic update
    if (isClubup) {
      setHasClubup(!wasActive);
      setClubupCount(prev => prev + (wasActive ? -1 : 1));
    } else {
      setHasAss(!wasActive);
      setAssCount(prev => prev + (wasActive ? -1 : 1));
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/feed/reactions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_type: targetType, target_id: targetId, reaction_type: reactionType }),
      });
      if (res.ok) {
        const data = await res.json();
        setClubupCount(data.clubup_count);
        setAssCount(data.ass_count);
        setHasClubup(data.has_clubup);
        setHasAss(data.has_ass);
      }
    } catch {
      // Revert on error
      if (isClubup) {
        setHasClubup(wasActive);
        setClubupCount(prev => prev + (wasActive ? 1 : -1));
      } else {
        setHasAss(wasActive);
        setAssCount(prev => prev + (wasActive ? 1 : -1));
      }
    }
  };

  return (
    <div className="reaction-bar">
      <button
        className={`reaction-btn ${hasClubup ? 'active' : ''}`}
        onClick={() => toggleReaction('clubup')}
        title={t('feed.clubup')}
      >
        <span className="reaction-icon">&#9889;</span>
        <span className="reaction-count">{clubupCount > 0 ? clubupCount : ''}</span>
      </button>

      <button
        className={`reaction-btn ${hasAss ? 'active' : ''}`}
        onClick={() => toggleReaction('ass')}
        title={t('feed.ass')}
      >
        <span className="reaction-icon">&#129451;</span>
        <span className="reaction-count">{assCount > 0 ? assCount : ''}</span>
      </button>

      <button className="reaction-btn" onClick={onCommentClick}>
        <span className="reaction-icon">&#128172;</span>
        <span className="reaction-count">{commentCount > 0 ? commentCount : ''}</span>
      </button>

      {aiComment && (
        <span className="ai-preview">{aiComment.slice(0, 60)}...</span>
      )}

      {recentReactors.length > 0 && (
        <div className="recent-reactors">
          {recentReactors.slice(0, 3).map(r => (
            r.user_profile_picture ? (
              <img
                key={r.user_id}
                src={r.user_profile_picture}
                alt=""
                className="reactor-avatar"
              />
            ) : null
          ))}
        </div>
      )}
    </div>
  );
};

export default ReactionBar;
